import { useState } from 'react'
import type { CatalogWatch } from '../../types/watch-monitor'

interface Props {
  initialData?: CatalogWatch
  onSave: (watch: CatalogWatch) => void
  onCancel: () => void
}

export default function WatchForm({ initialData, onSave, onCancel }: Props) {
  const [brand, setBrand] = useState(initialData?.brand || '')
  const [model, setModel] = useState(initialData?.model || '')
  const [reference, setReference] = useState(initialData?.reference || '')
  const [nickname, setNickname] = useState(initialData?.nickname || '')
  const [searchTermsText, setSearchTermsText] = useState(initialData?.search_terms.join(', ') || '')
  const [notes, setNotes] = useState(initialData?.notes || '')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!brand.trim() || !model.trim()) return

    onSave({
      brand: brand.trim(),
      model: model.trim(),
      reference: reference.trim(),
      nickname: nickname.trim(),
      search_terms: searchTermsText
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      notes: notes.trim(),
    })
  }

  const inputClass = 'w-full bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white text-sm rounded-lg px-3 py-2 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none placeholder-slate-400 dark:placeholder-slate-500'
  const labelClass = 'block text-slate-500 dark:text-slate-400 text-xs font-medium mb-1'

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Brand *</label>
          <input
            type="text"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            placeholder="e.g. Seiko, Omega, Rolex"
            className={inputClass}
            required
          />
        </div>
        <div>
          <label className={labelClass}>Model *</label>
          <input
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="e.g. Submariner, Speedmaster"
            className={inputClass}
            required
          />
        </div>
        <div>
          <label className={labelClass}>Reference</label>
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="e.g. 124060, SPB439"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Nickname</label>
          <input
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="e.g. Pepsi, Hulk, Moonwatch"
            className={inputClass}
          />
        </div>
      </div>
      <div>
        <label className={labelClass}>Search Terms (comma separated)</label>
        <textarea
          value={searchTermsText}
          onChange={(e) => setSearchTermsText(e.target.value)}
          placeholder="e.g. SPB439, SPB-439, SPB439J1, Seiko Prospex SPB439"
          className={`${inputClass} min-h-[60px] resize-y`}
          rows={2}
        />
        <p className="text-slate-400 dark:text-slate-600 text-xs mt-1">Include reference numbers with/without hyphens, common abbreviations, nicknames</p>
      </div>
      <div>
        <label className={labelClass}>Notes</label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Personal notes (not used in searches)"
          className={inputClass}
        />
      </div>
      <div className="flex items-center gap-3 pt-2">
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          {initialData ? 'Save Changes' : 'Add Watch'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 text-sm rounded-lg transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
